import { ProductListComponentBase } from '@utilities/product-list-component-base';
import { updateDocumentTitle, parseUrlParametersToActiveFilters, extractAndInjectStyles, cleanContentBlockElement, cleanupInjectedStyles } from '@utilities/theme';
import { nostoBuildQuery, nostoSearch, nostoTransformToShopifyProducts, nostoBuildFiltersFromShopify, nostoRecordSearchSubmit, nostoRecordAttribution } from '@utilities/nosto';

/**
 * ProductGrid - Unified custom element for managing product grids on search and collection pages
 *
 * This component handles both search results and collection product displays with comprehensive
 * filtering, sorting, and pagination support. It seamlessly integrates with both Shopify's
 * native search/collection APIs and Nosto's search platform.
 *
 * Responsibilities:
 * - Product data fetching from Shopify/Nosto APIs
 * - Product grid rendering and content block injection
 * - Pagination handling with browser history support
 * - URL state management for filters, sort, and pagination
 * - Event handling for filter changes (responds to filter-drawer events)
 * - Product-specific data transformations and lazy loading
 *
 * NOT Responsible For:
 * - Filter rendering or filter UI management (handled by FilterDrawer component)
 * - Filter metadata transformation for display (FilterDrawer transforms filter data)
 * - Filter option counts or availability (FilterDrawer fetches and manages this)
 *
 * Key Features:
 * - Dual data source support (Shopify/Nosto) with automatic transformation
 * - Real-time filtering and sorting with URL state management
 * - Server-side pagination with browser history support
 * - Content block injection for marketing content
 * - Lazy loading for performance optimization
 * - Filter drawer communication via events
 *
 * State Management:
 * - Maintains active filters, current page, and sort preferences
 * - Syncs state with URL parameters for shareable links
 * - Handles browser back/forward navigation
 * - Dispatches data events to notify FilterDrawer when product data includes filter metadata
 *
 * Data Flow:
 * 1. Initializes from URL parameters and configuration
 * 2. Fetches data from appropriate source (Shopify/Nosto)
 * 3. Transforms data to consistent format
 * 4. Renders products with templates
 * 5. Updates pagination and filter state
 * 6. Dispatches events for filter drawer synchronization
 *
 * @class
 * @extends HTMLElement
 * @customElement
 */
class ProductGrid extends ProductListComponentBase {
  /**
   * Creates an instance of ProductGrid
   * @constructor
   */
  constructor() {
    super();

    // Grid-specific configuration
    this.config.leverageUrl = true; // Default to true to maintain existing behavior

    // Grid-specific state properties
    this.state.currentPage = 1;
    this.state.activeFilters = {}; // Initialize as empty object

    // Grid-specific properties
    this.injectedStyles = new Set(); // Track injected style IDs to avoid duplicates
    this.cachedContentBlocks = null; // Cache processed content blocks per grid instance
    this.filterDebounceTimer = null; // Timer for debouncing filter changes

    // Bind event handlers to maintain context for proper cleanup
    this.boundHandlePopState = this.handlePopState.bind(this);

    // grid container
    this.container = this.closest("[data-product-grid-container]");
  }

  /**
   * Lifecycle callback when the element is added to the DOM
   * Either initializes the component immediately or sets up lazy loading
   * @returns {void}
   */
  async connectedCallback() {
    // Call base class connectedCallback first
    await super.connectedCallback();
  }

  /**
   * Lifecycle callback when the element is removed from the DOM
   * Cleans up observers, abort controllers, event listeners, and styles
   * @returns {void}
   */
  disconnectedCallback() {
    // ProductGrid-specific cleanup
    // Unregister from centralized event router
    if (window.productGridEventRouter) {
      window.productGridEventRouter.unregister(this);
    }

    // Clear debounce timer on disconnect
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }

    // Clean up event listeners
    window.removeEventListener("popstate", this.boundHandlePopState);

    // Clean up injected styles using utility function
    cleanupInjectedStyles(this.injectedStyles, { debug: this.allowDebug() });

    // Call base class disconnectedCallback last
    super.disconnectedCallback();
  }


  /**
   * Check if this is the main product grid on the page
   * Main grids control URL parameters and scroll to top of page
   * Non-main grids maintain independent state and scroll to their container
   * @returns {boolean}
   * @private
   */
  isMainGrid() {
    return this.dataset.mainProductGrid === "true";
  }

  /**
   * Scroll to the appropriate position based on grid type
   * Main grids scroll to top of page, non-main grids scroll to their container
   * @returns {void}
   * @private
   */
  scrollToGrid() {
    if (this.isMainGrid()) {
      // Main grid: scroll to top of page
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      if (this.allowDebug()) {
        console.log("Scrolled to top of page (main grid)");
      }
    } else {
      // Non-main grid: scroll to the grid container
      if (this.container) {
        this.container.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        if (this.allowDebug()) {
          console.log("Scrolled to grid container (non-main grid)", this.container);
        }
      } else {
        if (this.allowDebug()) {
          console.log("Grid container not found, skipping scroll");
        }
      }
    }
  }

  /**
   * Initialize the component with configuration parsing, state setup, and data loading
   * Handles URL parameter parsing, filter initialization, and auto-loading logic
   * @returns {void}
   * @private
   */
  /**
   * Initialize ProductGrid with URL state, events, and auto-loading
   */
  async init() {
    try {
      // Initialize configuration and templates first.
      // We intentionally defer the first loadData() call until after URL state is applied
      // so direct visits like ?page=2 fetch/render the correct page on first paint.
      this.parseConfiguration();
      this.initTemplates();

      // ProductGrid-specific initialization
      // STRICT: Require gridId for proper event scoping
      if (!this.config.gridId) {
        console.error("ProductGrid: Missing required gridId (config.gridId). ProductGrid must have a gridId for proper event scoping.", {
          element: this,
          config: this.config,
          note: "gridId should be the section/block ID, not the collection ID"
        });
        // Still allow initialization but warn that events won't be scoped correctly
      }

      // Initialize state from URL parameters (this must happen after parseConfiguration)
      this.initStateFromUrl();

      // Set initial page from config if not already set from URL
      if (!this.state.currentPage && this.config.currentPage) {
        this.state.currentPage = this.config.currentPage;
      }

      // Initialize active filters from current URL parameters
      this.parseUrlParametersToActiveFilters();

      // Initialize event listeners (must happen after URL state initialization)
      this.initEvents();

      // Register with centralized event router for optimized event handling
      if (window.productGridEventRouter && this.config.gridId) {
        window.productGridEventRouter.register(this);
      }

      this.classList.add("product-grid--initialized");

      // First data load must happen after URL-derived state initialization.
      await this.loadData();

      if (this.allowDebug()) {
        console.log("ProductGrid initialized", this);
      }
    } catch (error) {
      console.error("ProductGrid initialization error:", error);
      this.showErrorState("Failed to initialize product grid");
    }
  }

  /**
   * Set component state for CSS styling
   * @param {string} state
   */
  setComponentState(state) {
    this.setGridState(state);
  }

  /**
   * Render products in grid layout with content blocks
   * @param {Array} products - Product data array
   * @param {string} vendor - Data source vendor
   */
  renderProducts(products, vendor) {
    this.renderProductGrid(products, vendor);
  }

  /**
   * Get the container element for product cards
   * @returns {HTMLElement} Container element
   */
  getContainerElement() {
    return this.elements.resultsContainer;
  }

  /**
   * Initialize DOM element references
   * @returns {void}
   * @private
   */
  initTemplates() {
    this.elements.templates = {};
    this.elements.loadingState = this.querySelector("[data-loading-state]");
    this.elements.errorState = this.querySelector("[data-error-state]");
    this.elements.emptyState = this.querySelector("[data-empty-state]");
    this.elements.resultsContainer = this.querySelector(
      "[data-results-container]"
    );
    this.elements.paginationContainer = this.querySelector(
      "[data-pagination-container]"
    );
    // Note: paginationControls element will be available after pagination snippet renders
    this.elements.paginationControls = null;

    // Product grid template elements only (filter templates are handled by filter-drawer)
    this.elements.templates.productGrid = this.querySelector(
      "template#product-grid-template"
    );
    this.elements.templates.productCardComponent = this.querySelector(
      "template#product-card-component-template"
    );
    this.elements.templates.contentBlocks = this.querySelector(
      "template#product-grid-template-content-blocks"
    );
    this.elements.templates.noResults = this.querySelector(
      "template#no-results-template"
    );

    // Additional elements for layout
    this.elements.gridTitle = this.querySelector("[data-grid-title]");
    this.elements.resultsCount = this.querySelector("[data-results-count]");
  }

  /**
   * Initialize event listeners
   * @returns {void}
   * @private
   */
  initEvents() {
    // Filter and pagination events are handled by centralized ProductGridEventRouter in global.js
    // See global.js for the router implementation that routes events to matching ProductGrids
    // This eliminates duplicate document-level listeners when multiple ProductGrids exist

    // Only instance-specific listeners are set up here:
    // Listen for browser back/forward button events (instance-specific)
    window.addEventListener("popstate", this.boundHandlePopState);

    // Listen for vendor changes from theme utilities (broadcasts to all grids, not identifier-based)
    document.addEventListener(
      "vendor:changed",
      this.handleVendorChange.bind(this)
    );

    // Listen for processing flag changes from theme utilities
    document.addEventListener(
      "processing:changed",
      this.handleProcessingChange.bind(this)
    );

    // Notify filter drawer about our config when needed
    this.notifyFilterDrawer();
  }

  /**
   * Check if a FilterDrawer event is intended for this ProductGrid
   *
   * FilterDrawer identifier formats:
   * - Section context: "collection-filter-drawer-{section_id}"
   * - Block context: "{collection-id}-{section-id}-{block-id}"
   *
   * ProductGrid config.gridId is:
   * - Section context: section ID
   * - Block context: block ID
   *
   * Matching logic: Check if identifier ends with this grid's gridId (with or without separator)
   *
   * STRICT MODE: Requires identifier match - no fallback behavior
   *
   * @param {CustomEvent} event
   * @returns {boolean}
   * @private
   */
  shouldRespondToFilterDrawerEvent(event) {
    const eventIdentifier = event.detail?.identifier;

    // STRICT: Require identifier in event
    if (!eventIdentifier) {
      if (this.allowDebug()) {
        console.warn("ProductGrid: FilterDrawer event missing identifier - ignoring", event);
      }
      return false;
    }

    // STRICT: Require grid to have a gridId
    if (!this.config.gridId) {
      if (this.allowDebug()) {
        console.error("ProductGrid: Grid missing gridId (config.gridId) - cannot match events", {
          gridId: this.config.gridId,
          eventIdentifier,
          configId: this.config.id,
          note: "config.id is collection ID, config.gridId is section/block ID for event scoping"
        });
      }
      return false;
    }

    // Both identifiers are now handleized in Liquid, so we can compare directly
    // FilterDrawer identifier format: "collection-filter-drawer-{section-id}" or "{collection-id}-{section-id}-{block-id}"
    // ProductGrid gridId format: handleized section/block ID
    const gridId = String(this.config.gridId).toLowerCase();
    const identifierLower = String(eventIdentifier).toLowerCase();

    // Check if identifier ends with grid ID (with or without separator)
    // This works for both formats:
    // - "collection-filter-drawer-template-25787538407787__product-grid" ends with "template-25787538407787__product-grid" ✓
    // - "456-789-template-25787538407787__product-grid" ends with "template-25787538407787__product-grid" ✓
    const matches = identifierLower.endsWith(gridId) || identifierLower.endsWith(`-${gridId}`);

    if (!matches && this.allowDebug()) {
      console.log("ProductGrid: FilterDrawer event identifier mismatch - ignoring", {
        eventIdentifier,
        identifierLower,
        gridId,
        matches
      });
    }

    return matches;
  }

  /**
   * Notify filter drawer about our configuration
   * @private
   */
  notifyFilterDrawer() {
    if (!this.shouldAutoLoad()) return;
    const event = new CustomEvent("product-grid:filter-request", {
      detail: {
        context: this.config.context,
        config: this.config,
      },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Determine if component should auto-load data based on context and configuration
   * Search context requires terms, collection context requires handle or id
   * @returns {boolean} True if component should automatically load data
   * @private
   */
  shouldAutoLoad() {
    if (this.config.context === "search") {
      return this.config.terms && this.config.terms.trim().length > 0;
    } else if (this.config.context === "collection") {
      return this.config.handle || this.config.id;
    }
    return false;
  }

  /**
   * Load data from appropriate source (Shopify or Nosto) based on vendor configuration
   * Handles loading states, error handling, and data transformation
   * @returns {Promise<void>}
   */
  async loadData() {
    if (this.allowDebug()) {
      console.log("loadData", this);
    }
    if (this.state.isLoading) return;

    this.setState({ isLoading: true, hasError: false });
    this.showLoadingState();

    try {
      // Abort any existing request
      this.abortController?.abort();
      this.abortController = new AbortController();

      const vendor = this.getDataVendor();
      let data;

      if (this.allowDebug()) {
        console.log("fetching data from", vendor);
      }

      if (vendor === "nosto") {
        data = await this.fetchNostoData();
      } else {
        data = await this.fetchShopifyData();
      }

      this.setState({ data, isLoading: false });

      this.renderResults(data);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("ProductGrid data loading error:", error);
        this.setState({ hasError: true, isLoading: false });

        // Use user-friendly error message if available, fallback to generic message
        let errorMessage = error.userMessage || error.message || "Unable to load products. Please try again.";

        // Log technical details for debugging while showing user-friendly message
        if (this.allowDebug()) {
          console.error("ProductGrid technical error details:", {
            code: error.code,
            userMessage: error.userMessage,
            debugMessage: error.debugMessage,
            originalError: error.originalError,
            details: error.details
          });
        }

        this.showErrorState(errorMessage);
      }
    }
  }

  /**
   * Fetch data from Shopify
   * @returns {Promise<Object>}
   * @private
   */
  async fetchShopifyData() {
    const url = this.buildShopifyUrl();

    const response = await fetch(url, {
      signal: this.abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();
    return JSON.parse(text);
  }

  /**
   * Fetch data from Nosto using the utilities system.
   *
   * Builds filters from active Shopify filters, constructs a Nosto query,
   * executes the search, and transforms the response to Shopify format.
   * Handles sort options with special logic for best-selling, manual, and relevance sorts.
   *
   * @returns {Promise<Object>} Shopify-compatible product data with pagination and filters
   * @throws {Error} When Nosto search fails
   * @private
   */
  async fetchNostoData() {
    try {
      const filters = nostoBuildFiltersFromShopify(
        this.state.activeFilters,
        { debug: this.allowDebug() }
      );

      const defaultSort = this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance");
      let nostoSortBy = this.config.sortBy || defaultSort;
      let shouldApplySort = true;

      if (nostoSortBy === "best-selling") {
        shouldApplySort = false;
        if (this.allowDebug()) {
          console.log("Nosto Wrapper: Not applying best-selling sort (no sort param sent)");
        }
      } else if (nostoSortBy === "manual") {
        shouldApplySort = false;
        if (this.allowDebug()) {
          console.log("Nosto Wrapper: Not applying manual (Featured) sort (no sort param sent)");
        }
      } else if (nostoSortBy === "relevance") {
        shouldApplySort = false;
        if (this.allowDebug()) {
          console.log("Nosto Wrapper: Not applying relevance sort (no sort param sent)");
        }
      }

      const queryParams = {
        context: this.config.context,
        terms: this.config.terms,
        categoryId: this.config.id,
        type: "products",
        size: this.config.settings.productsPerPage || 24,
        from:
          (this.state.currentPage - 1) *
          (this.config.settings.productsPerPage || 24),
        filters,
        allowSoldOutStateProducts: this.config.settings.allowSoldOutStateProducts || false,
      };

      // Only include sortBy if we should apply sort
      if (shouldApplySort) {
        queryParams.sortBy = nostoSortBy;
      }

      const queryConfig = nostoBuildQuery(queryParams);

      // Record search submit BEFORE the search request to mark as organic search
      // This is required for accurate Nosto analytics - see: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
      if (this.config.context === "search" && this.config.terms) {
        try {
          await nostoRecordSearchSubmit(this.config.terms, { debug: this.allowDebug() });
        } catch (e) {
          // Non-blocking - don't let tracking errors affect search functionality
          if (this.allowDebug()) {
            console.error('Nosto recordSearchSubmit error:', e);
          }
        }
      }

      const response = await nostoSearch(
        queryConfig.query,
        queryConfig.options,
        { debug: this.allowDebug(), trigger: 'product-grid' }
      );

      // DUAL ATTRIBUTION: Record page-level attribution events for accurate Nosto dashboard reporting
      // IS (Internal Search) for search pages, VC (View Category) for collection pages
      // See: https://docs.nosto.com/techdocs/apis/frontend/js-apis/record-attribution
      // Note: Fire-and-forget pattern to prevent blocking page load
      if (this.config.context === "search" && this.config.terms) {
        // Record Internal Search attribution (non-blocking)
        nostoRecordAttribution({
          type: "is", // Internal Search
          target: this.config.terms,
          refType: "os", // Onsite search
          debug: this.allowDebug(),
          timeout: 3000 // 3 second timeout
        }).catch((e) => {
          // Silently handle attribution errors without blocking
          if (this.allowDebug()) {
            console.error('Nosto recordAttribution error:', e);
          }
        });
      } else if (this.config.context === "collection" && this.config.id) {
        // Record View Category attribution (non-blocking)
        nostoRecordAttribution({
          type: "vc", // View Category
          target: String(this.config.id),
          refType: "cmp", // Category merchandising
          debug: this.allowDebug(),
          timeout: 3000 // 3 second timeout
        }).catch((e) => {
          // Silently handle attribution errors without blocking
          if (this.allowDebug()) {
            console.error('Nosto recordAttribution error:', e);
          }
        });
      }

      // Note: Search/category analytics are also handled by the `track` option passed to api.search()
      // via nostoBuildQuery. Additionally, we call recordSearchSubmit above for organic search marking.
      // Click and add-to-cart events are tracked via dual attribution in ProductCard.
      // See: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
      const hasActiveFilters = this.state.activeFilters &&
        Object.keys(this.state.activeFilters).length > 0 &&
        Object.values(this.state.activeFilters).some((values) =>
          Array.isArray(values) && values.length > 0,
        );
      const hasActiveSort =
        !!this.config.sortBy && this.config.sortBy !== defaultSort;
      const spotlightEnabled =
        this.config.context === "collection" &&
        !hasActiveFilters &&
        !hasActiveSort;

      return nostoTransformToShopifyProducts(response, {
        context: this.config.context,
        terms: this.config.terms,
        currentPage: this.state.currentPage,
        pageSize: this.config.settings.productsPerPage || 24,
        sortBy: this.config.sortBy || defaultSort, // Keep original sort for URL/UI purposes
        spotlightConfig: {
          enabled: spotlightEnabled,
          colors: this.config.settings?.colorSpotlightColors || [],
          colorFamily: this.config.settings?.colorSpotlightColorFamily || "",
        },
        generatePaginationParts: this.generatePaginationParts.bind(this),
        getSortOptions: this.getSortOptions.bind(this)
      });
    } catch (error) {
      throw new Error(`Nosto search failed: ${error.message}`);
    }
  }

  /**
   * Build Shopify API URL based on context
   * @returns {string}
   * @private
   */
  buildShopifyUrl() {
    const baseUrl = window.location.origin;
    let path;
    const params = new URLSearchParams();

    if (this.config.context === "search") {
      path = window.theme.routes.search_url;
      if (this.config.terms) params.set("q", this.config.terms);
      if (this.config.type) params.set("type", this.config.type);
    } else if (this.config.context === "collection") {
      path = `${window.theme.routes.collections_url}/${this.config.handle}`;
    }

    // Add common parameters
    params.set("view", "csr-json");
    if (this.state.currentPage > 1) params.set("page", this.state.currentPage);
    if (this.config.settings.productsPerPage) params.set("products_per_page", this.config.settings.productsPerPage);

    // Add active filters
    this.addActiveFiltersToParams(params);

    // Add sort parameter
    if (this.config.sortBy) params.set("sort_by", this.config.sortBy);

    return `${baseUrl}${path}?${params.toString()}`;
  }

  /**
   * Add active filters to URL parameters (for Shopify)
   * @param {URLSearchParams} params
   * @private
   */
  addActiveFiltersToParams(params) {
    if (this.state.activeFilters && Object.keys(this.state.activeFilters).length > 0) {
      Object.entries(this.state.activeFilters).forEach(([key, values]) => {
        if(values && values.length > 0) {
          values.forEach((value) => {
            params.append(key, value);
          });
        }
      });
    }
  }

  /**
   * Generate pagination parts
   * @param {number} totalPages
   * @param {number} currentPage
   * @returns {Array}
   * @private
   */
  generatePaginationParts(totalPages, currentPage) {
    if (totalPages <= 1) return [];

    const parts = [];
    const showEllipsis = totalPages > 5;

    if (showEllipsis) {
      // Show first page
      parts.push({
        title: 1,
        url: this.buildPageUrl(1),
        isLink: currentPage !== 1,
      });

      if (currentPage > 3) {
        parts.push({
          title: "&hellip;",
          isLink: false,
        });
      }

      // Show current page and neighbors
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        parts.push({
          title: i,
          url: this.buildPageUrl(i),
          isLink: i !== currentPage,
        });
      }

      if (currentPage < totalPages - 2) {
        parts.push({
          title: "&hellip;",
          isLink: false,
        });
      }

      // Show last page
      if (totalPages > 1) {
        parts.push({
          title: totalPages,
          url: this.buildPageUrl(totalPages),
          isLink: currentPage !== totalPages,
        });
      }
    } else {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        parts.push({
          title: i,
          url: this.buildPageUrl(i),
          isLink: i !== currentPage,
        });
      }
    }

    return parts;
  }

  /**
   * Build URL for specific page
   * @param {number} page
   * @returns {string}
   * @private
   */
  buildPageUrl(page) {
    const currentUrl = new URL(window.location);
    const params = new URLSearchParams(currentUrl.search);

    if (page > 1) {
      params.set("page", page);
    } else {
      params.delete("page");
    }

    return `${currentUrl.pathname}?${params.toString()}`;
  }

  /**
   * Get sort options based on context
   * @returns {Array}
   * @private
   */
  getSortOptions() {
    if (this.config.context === "search") {
      // Search context: Only relevance and price sorting
      return [
        ["relevance", "Relevance"],
        ["price-ascending", "Price, low to high"],
        ["price-descending", "Price, high to low"],
      ];
    } else if (this.config.context === "collection") {
      // Collection context: Featured, best selling, and price sorting
      return [
        ["manual", "Featured"],
        ["best-selling", "Best Selling"],
        ["price-ascending", "Price, low to high"],
        ["price-descending", "Price, high to low"],
      ];
    }

    // Fallback for other contexts
    return [
      ["relevance", "Relevance"],
      ["price-ascending", "Price, low to high"],
      ["price-descending", "Price, high to low"],
    ];
  }

  /**
   * Render results to DOM
   * @param {Object} data
   * @private
   */
  renderResults(data) {
    if (this.allowDebug()) {
      console.log("rendering product grid with the given data", data);
    }

    // Set vendor data attribute from the rendering data
    this.dataset.vendor = data.vendor;

    if (!data.products || data.products.length === 0) {
      if(this.config.settings.hideOnEmptyProducts) {
        this.hideOnEmptyProducts();
      }
      this.showEmptyState();
      // Still dispatch event with empty data for filter drawer to update
      this.dispatchDataUpdateEvent(data);
      return;
    }

    // Set results state
    this.setGridState("results");

    // Clear the results container first
    if (this.elements.resultsContainer) {
      // Use replaceChildren() instead of innerHTML for better performance
      // replaceChildren() is faster and doesn't trigger HTML parsing
      this.elements.resultsContainer.replaceChildren();
    }

    // Render products
    if (this.elements.resultsContainer && this.elements.templates.productGrid) {
      this.renderProducts(data.products, data.vendor);
    }

    // Render pagination using the pagination-controls component
    if (data.paginate) {
      this.renderPagination(data.paginate);
    }

    // Dispatch event to notify filter drawer of updated data
    this.dispatchDataUpdateEvent(data);
  }

  /**
   * Render products grid
   * @param {Array} products
   * @private
   */
  renderProducts(products, vendor) {
    const template = this.elements.templates.productGrid;
    const container = this.elements.resultsContainer;

    if (!template || !container) return;

    // Clone template and populate with products
    const templateClone = template.content.cloneNode(true);
    const productContainer = templateClone.querySelector(
      "[data-products-container]"
    );

    if (productContainer) {
      // Use DocumentFragment for batching DOM operations
      const fragment = document.createDocumentFragment();

      // Early exit: Check if content blocks should be shown BEFORE processing
      let contentBlocksMap = null;
      if (this.shouldShowContentBlocks()) {
        const contentBlocks = this.getContentBlocks();

        // Pre-index content blocks by position using Map for O(1) lookup
        contentBlocksMap = new Map();
        contentBlocks.forEach(block => {
          const index = block.index;
          if (!contentBlocksMap.has(index)) {
            contentBlocksMap.set(index, []);
          }
          contentBlocksMap.get(index).push(block);
        });

        if (this.allowDebug()) {
          console.log("Content blocks to render:", contentBlocks.length, "indexed in Map");
        }
      } else {
        if (this.allowDebug()) {
          console.log("Content blocks skipped: shouldShowContentBlocks() returned false");
        }
      }

      products.forEach((product, index) => {
        // Check if there's a content block for this index (1-based) using Map lookup
        const productIndex = index + 1;
        const contentBlocksAtIndex = contentBlocksMap?.get(productIndex) || [];

        // Add content blocks first if they exist for this index (marketing content injection)
        if (contentBlocksAtIndex.length > 0) {
          if (this.allowDebug()) {
            console.log(`Inserting ${contentBlocksAtIndex.length} content ${contentBlocksAtIndex.length > 1 ? 'blocks' : 'block'} at index ${productIndex}`);
          }
          contentBlocksAtIndex.forEach(contentBlock => {
            // Clone element since it may be reused
            fragment.appendChild(contentBlock.element.cloneNode(true));
          });
        }

        // Add the product (pass index for lazy loading logic)
        const productElement = this.createProductElement(product, vendor);
        if (productElement) {
          fragment.appendChild(productElement);
        }
      });

      // Check for content blocks with indices greater than the last product index
      if (contentBlocksMap) {
        const lastProductIndex = products.length;
        const trailingIndices = Array.from(contentBlocksMap.keys())
          .filter(index => index > lastProductIndex)
          .sort((a, b) => a - b);

        if (trailingIndices.length > 0) {
          if (this.allowDebug()) {
            console.log(`Inserting ${trailingIndices.length} trailing content ${trailingIndices.length > 1 ? 'blocks' : 'block'} after last product`);
          }
          trailingIndices.forEach(index => {
            contentBlocksMap.get(index).forEach(contentBlock => {
              // Clone element since it may be reused
              fragment.appendChild(contentBlock.element.cloneNode(true));
            });
          });
        }
      }

      // Single DOM operation: append all elements at once
      productContainer.appendChild(fragment);
    }

    // Update the banner search count and title if they exist
    if (
      this.elements.resultsCount &&
      this.state.data &&
      this.state.data.resultsCount !== undefined
    ) {
      this.elements.resultsCount.textContent = this.state.data.resultsCount;
    }

    // Update the grid title with results information
    if (this.elements.gridTitle && this.config.context === "search") {
      const resultsCount = this.state.data?.resultsCount || 0;
      const searchTerms = this.config.terms || "";

      if (resultsCount > 0) {
        this.elements.gridTitle.innerHTML = `<span class="search__count">${resultsCount}</span>&nbsp;results for "${searchTerms}"`;
      } else {
        this.elements.gridTitle.textContent = `No results found for "${searchTerms}"`;
      }
    }

    // Update document title for search context
    this.updateDocumentTitleProxy();

    container.appendChild(templateClone);
  }

  /**
   * Determine if content blocks should be shown
   * @returns {boolean}
   * @private
   */
  shouldShowContentBlocks() {
    if(!this.config.settings.showContentBlocks) {
      return false;
    }
    // Don't show content blocks if not on page 1
    if (this.state.currentPage !== 1) {
      if (this.allowDebug()) {
        console.log("Content blocks hidden: not on page 1");
      }
      return false;
    }

    // Don't show content blocks if there are active filters (excluding availability filters)
    if (this.hasNonAvailabilityActiveFilters()) {
      if (this.allowDebug()) {
        console.log("Content blocks hidden: active filters present");
      }
      return false;
    }

    return true;
  }

  /**
   * Check if there are active filters other than availability filters
   * @returns {boolean}
   * @private
   */
  hasNonAvailabilityActiveFilters() {
    const availabilityFilters = [
      "filter.p.m.greyson.availability",
      "filter.v.m.greyson.availability"
    ];

    if (!this.state.activeFilters) {
      return false;
    }

    // Check if any filter is active that's not an availability filter
    for (const [filterKey, filterValues] of Object.entries(
      this.state.activeFilters
    )) {
      if (
        !availabilityFilters.includes(filterKey) &&
        Array.isArray(filterValues) &&
        filterValues.length > 0
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Extract content blocks from template and parse their positions
   * Caches results per grid instance to avoid re-processing
   * @returns {Array} Array of content block objects with index and element
   * @private
   */
  getContentBlocks() {
    // Return cached content blocks if available
    if (this.cachedContentBlocks !== null) {
      if (this.allowDebug()) {
        console.log("Using cached content blocks:", this.cachedContentBlocks.length);
      }
      return this.cachedContentBlocks;
    }

    const template = this.elements.templates.contentBlocks;
    if (!template) {
      if (this.allowDebug()) {
        console.log("No content blocks template found");
      }
      // Cache empty array to avoid re-checking
      this.cachedContentBlocks = [];
      return [];
    }

    const contentBlocks = [];
    const clone = template.content.cloneNode(true);

    // First, extract and inject any style tags
    extractAndInjectStyles(clone, this.injectedStyles, { debug: this.allowDebug() });

    // Get all direct children of the template content
    const children = Array.from(clone.children);

    children.forEach((child) => {
      // Look for various ways the position might be encoded
      let blockIndex = null;

      // Method 1: Check for data-position attribute (preferred method from template)
      if (child.hasAttribute("data-position")) {
        blockIndex = parseInt(child.getAttribute("data-position"), 10);
      }

      if (blockIndex && blockIndex > -1) {
        // Clean the element by removing any remaining style tags
        const cleanedElement = cleanContentBlockElement(
          child.cloneNode(true)
        );

        contentBlocks.push({
          index: blockIndex,
          element: cleanedElement,
        });

        if (this.allowDebug()) {
          console.log(`Content block found for index ${blockIndex}`);
        }
      } else {
        if (this.allowDebug()) {
          console.log(
            "Content block found but no valid index determined, skipping"
          );
        }
      }
    });

    // Sort content blocks by index to ensure proper order
    contentBlocks.sort((a, b) => a.index - b.index);

    if (this.allowDebug()) {
      console.log(`Total content blocks extracted: ${contentBlocks.length}`);
      contentBlocks.forEach((block) => {
        console.log(
          `- Block at index ${block.index}:`,
          block.element.tagName,
          block.element.className
        );
      });
    }

    // Cache the processed content blocks
    this.cachedContentBlocks = contentBlocks;
    return contentBlocks;
  }


  /**
   * Create product element from data
   * @param {Object} product
   * @param {string} vendor - The vendor of the product ("shopify" or "nosto")
   * @returns {HTMLElement|null}
   * @private
   */
  createProductElement(product, vendor) {
    if(this.allowDebug()) {
      console.log("createProductElement", { product, vendor });
    }
    // Try to use product-card component first
    const productCardElement = this.createProductCardComponent(product, vendor);
    if (productCardElement) {
      return productCardElement;
    }

    return null;
  }

  /**
   * Create product card component from data
   * @param {Object} product
   * @param {string} vendor - The vendor of the product ("shopify" or "nosto")
   * @returns {HTMLElement|null}
   * @private
   */
  createProductCardComponent(product, vendor) {
    const template = this.elements.templates.productCardComponent;
    if (!template) return null;

    const clone = template.content.cloneNode(true);
    const productCardElement = clone.querySelector("product-card");

    if (!productCardElement) return null;

    // Set up the product card component data attributes

    const defaultVariantId = product.defaultVariantId || null;
    const productUrl = `${product.url}${defaultVariantId ? `?variant=${defaultVariantId}` : ''}`;
    const fetchUrl = this.buildProductFetchUrl(product, defaultVariantId);

    // Generate unique component ID for heatmap tracking
    // Format: {context}-product-card-{product.id}
    // collection context is "collection-{collection.id}"
    // search context is "search-product-card-{product.id}"
    // fallback context is "product-grid"
    const componentId = this.config.context === "collection"
      ? `collection-${this.config.id}-product-card-${product.id}`
      : this.config.context === "search"
        ? `search-product-card-${product.id}`
        : `product-grid-product-card-${product.id}`;

    // gridId is still used for form ID scoping (functional, not heatmap)
    const sectionId = this.config.gridId || this.config.id || "product-grid";

    // Set data attributes for client-side rendering
    productCardElement.id = componentId;
    // productCardElement.classList.add("debug");
    productCardElement.dataset.productId = product.id; // Required for Nosto click tracking
    productCardElement.dataset.fetchUrl = fetchUrl;
    productCardElement.dataset.productUrl = productUrl;
    productCardElement.dataset.defaultVariantId = defaultVariantId;
    productCardElement.dataset.productFormId = `quick-add-${sectionId}${product.id}`;
    productCardElement.dataset.vendor = vendor;

    // Set Nosto context for attribution tracking
    if (vendor === "nosto") {
      const context = this.config.context === "search"
        ? "product-grid-search"
        : "product-grid-collection";
      productCardElement.dataset.nostoContext = context;
    }

    productCardElement.classList.add("lazy-load");

    /**
     * Sold Out State Logic
     * This is extra post-processing logic. Ideally the product sourcing platform would handle and filter these out.
     * A product grid may show sold out products with a "Sold Out" state.
     * The product grid settings.allow_sold_out_state_products must be true.
     * The product must have the following tags:
     * - `shopify:unavailable`
     * - `greyson:available`
     * - `ignore-oos-logic`
     *
     * If all of the above are true, then the productCardElement will be given a dataset of dataset.showSoldOutState = true
     * If the product has the above tags, but the product grid settings.allow_sold_out_state_products is false, then the productCardElement will be skipped.
     */
    if (product.tags.includes("shopify:unavailable") && product.tags.includes("greyson:available") && product.tags.includes("ignore-oos-logic")) {
      if (this.config.settings.allowSoldOutStateProducts) {
        productCardElement.dataset.showSoldOutState = true;
      } else {
        return null;
      }
    }

    return clone;
  }

  /**
   * Build product fetch URL for client-side rendering
   * @param {Object} product
   * @returns {string}
   * @private
   */
  buildProductFetchUrl(product, defaultVariantId = null) {
    // For client-side rendering, we'll use the product URL with the product-json view parameter
    // Handle cases where the product URL already contains query parameters
    const url = new URL(product.url, window.location.origin);
    if (defaultVariantId) {
      url.searchParams.set("variant", defaultVariantId);
    }
    url.searchParams.set("view", "product-json");
    return url.toString();
  }

  /**
   * Render pagination using the pagination-controls component
   * Dispatches event-based updates to ALL PaginationControls associated with this ProductGrid
   * Uses identifier-based event scoping (not DOM-based) to allow PaginationControl anywhere in DOM
   * @param {Object} paginate
   * @private
   */
  renderPagination(paginate) {
    if (!paginate) return;

    // STRICT: Require gridId for proper event scoping
    if (!this.config.gridId) {
      if (this.allowDebug()) {
        console.warn("ProductGrid: Cannot dispatch pagination update - missing gridId", {
          gridId: this.config.gridId,
          configId: this.config.id
        });
      }
      return;
    }

    // Build base URL without query parameters for pagination
    let baseUrl = window.location.pathname;

    // For collection context, use collection URL
    if (this.config.context === "collection" && this.config.url) {
      baseUrl = this.config.url;
    }

    if (this.allowDebug()) {
      console.log(
        "Dispatching pagination update event:",
        paginate,
        "baseUrl:",
        baseUrl,
        "isMainGrid:",
        this.isMainGrid(),
        "gridId:",
        this.config.gridId
      );
    }

    // Dispatch event for PaginationControls to listen to (event-based, not direct DOM queries)
    // All PaginationControls with matching identifier will receive and process this event
    const event = new CustomEvent("product-grid:pagination-updated", {
      detail: {
        paginate,
        baseUrl,
        identifier: this.config.gridId,
        isMainGrid: this.isMainGrid()
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  /**
   * Update component state
   * @param {Object} newState
   * @private
   */
  setState(newState) {
    this.state = { ...this.state, ...newState };
  }

  /**
   * Show loading state
   * @private
   */
  showLoadingState() {
    this.setGridState("loading");
  }

  /**
   * Show error state
   * @param {string} message
   * @private
   */
  showErrorState(message) {
    this.setGridState("error");
    if (this.elements.errorState) {
      const messageElement = this.elements.errorState.querySelector(
        "[data-error-message]"
      );
      if (messageElement) {
        messageElement.textContent =
          message || "An error occurred while loading products.";
      }
    }
  }

  /**
   * Show empty state
   * @private
   */
  showEmptyState() {
    this.setGridState("empty");
    // Update document title for empty state
    this.updateDocumentTitleProxy();
  }

  hideOnEmptyProducts() {
    this.classList.add("hidden");
    if(this.container) this.container.classList.add("hidden");
  }

  /**
   * Set grid state using data-state attribute
   * @param {string} state - 'loading', 'error', 'empty', 'results', or 'initial'
   * @private
   */
  setGridState(state) {
    // Set state using data attribute
    this.dataset.state = state;

    if (this.allowDebug()) {
      console.log("Grid state changed to:", state);
    }
  }

  /**
   * Update document title using theme utilities
   * @private
   */
  updateDocumentTitleProxy() {
    // Only compile title for search context
    if (this.config.context !== "search") {
      return null;
    }

    const searchTerms = this.config.terms || "";
    const resultsCount = this.state.data?.resultsCount || 0;

    let newTitle = "Search";

    if (searchTerms) {
      newTitle = `Search: ${resultsCount} results found for "${searchTerms}"`;
    }

    if (newTitle) {
      updateDocumentTitle(newTitle);

      if (this.allowDebug()) {
        console.log("Updated document title:", document.title);
      }
    }
  }

  /**
   * Check if grid is filtered or sorted (price sorting)
   * @returns {boolean} True if filters are active or price sorting is active
   * @private
   */
  isFilteredOrSorted() {
    // Check for active filters
    const hasActiveFilters = this.state.activeFilters &&
      Object.keys(this.state.activeFilters).length > 0 &&
      Object.values(this.state.activeFilters).some(values =>
        Array.isArray(values) && values.length > 0
      );

    // Check for price sorting
    const isPriceSorted = this.config.sortBy === 'price-ascending' ||
                         this.config.sortBy === 'price-descending';

    return hasActiveFilters || isPriceSorted;
  }

  /**
   * Update filtered state on the DOM element
   * Sets data-filtered attribute and product-grid--filtered class based on filtered/sorted state
   * @private
   */
  updateFilteredState() {
    const isFiltered = this.isFilteredOrSorted();

    if (isFiltered) {
      this.setAttribute('data-filtered', 'true');
      this.classList.add('product-grid--filtered');
    } else {
      this.removeAttribute('data-filtered');
      this.classList.remove('product-grid--filtered');
    }

    if (this.allowDebug()) {
      console.log('ProductGrid: Updated filtered state:', isFiltered);
    }
  }

  /**
   * Handle filter change events from FilterDrawer
   * Debounced to prevent rapid re-renders
   * @param {CustomEvent} event
   * @private
   */
  async handleFilterChange(event) {
    // Check if this event is intended for this ProductGrid
    if (!this.shouldRespondToFilterDrawerEvent(event)) {
      if (this.allowDebug()) {
        console.log("handleFilterChange: Ignoring event - not for this grid", {
          eventIdentifier: event.detail?.identifier,
          gridId: this.config.id
        });
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("handleFilterChange", event.detail);
    }

    const { filterName, filterValue, action } = event.detail;

    // Ensure activeFilters is initialized
    if (!this.state.activeFilters) {
      this.state.activeFilters = {};
    }

    if (!this.state.activeFilters[filterName]) {
      this.state.activeFilters[filterName] = [];
    }

    if (action === "add") {
      if (!this.state.activeFilters[filterName].includes(filterValue)) {
        this.state.activeFilters[filterName].push(filterValue);
      }
    } else if (action === "remove") {
      this.state.activeFilters[filterName] = this.state.activeFilters[
        filterName
      ].filter((value) => value !== filterValue);

      // Remove empty filter arrays
      if (this.state.activeFilters[filterName].length === 0) {
        this.state.activeFilters[filterName] = undefined;
      }
    }

    if (this.allowDebug()) {
      console.log("Updated activeFilters:", this.state.activeFilters);
    }

    // Update filtered state
    this.updateFilteredState();

    // Reset to first page when filters change
    this.state.currentPage = 1;

    // Debounce filter changes to prevent rapid re-renders
    // Clear any existing timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }

    // Set new timer (300ms debounce - same as predictive search)
    this.filterDebounceTimer = setTimeout(async () => {
      // Update URL history before loading data
      this.updateUrlHistory();

      await this.loadData();

      // Clear timer reference
      this.filterDebounceTimer = null;
    }, 300);
  }

  /**
   * Handle pagination change events
   * Main grids update URL history, non-main grids only update internal state
   * @param {CustomEvent} event
   * @private
   */
  async handlePaginationChange(event) {
    if (this.allowDebug()) {
      console.log("handlePaginationChange", event);
    }

    // STRICT: Check if this event is intended for this ProductGrid
    const eventIdentifier = event.detail?.identifier;
    if (eventIdentifier) {
      // If event has identifier, verify it matches this grid
      if (!this.config.gridId) {
        if (this.allowDebug()) {
          console.error("ProductGrid: Pagination event has identifier but grid missing config.gridId - ignoring", {
            eventIdentifier,
            gridId: this.config.gridId,
            configId: this.config.id,
            note: "config.id is collection ID, config.gridId is section/block ID for event scoping"
          });
        }
        return;
      }

      const gridId = String(this.config.gridId).toLowerCase();
      const identifierLower = String(eventIdentifier).toLowerCase();

      // Match if identifier exactly equals gridId or ends with gridId (with or without separator)
      // This handles both simple identifiers (section ID) and compound identifiers (collection-section-block)
      const matches = identifierLower === gridId || identifierLower.endsWith(`-${gridId}`) || identifierLower.endsWith(gridId);

      if (!matches) {
        if (this.allowDebug()) {
          console.log("ProductGrid: Pagination event identifier mismatch - ignoring", {
            eventIdentifier,
            gridId,
            matches
          });
        }
        return;
      }
    } else {
      // STRICT: Require identifier in pagination events
      if (this.allowDebug()) {
        console.warn("ProductGrid: Pagination event missing identifier - ignoring", event);
      }
      return;
    }

    const { page } = event.detail;
    this.state.currentPage = page;

    // update the data-page attribute
    this.dataset.page = page;

    // update the data-products-container attribute
    const productContainer = this.querySelector("[data-products-container]");
    if(productContainer) {
      productContainer.dataset.page = page;
    }

    // Only update URL history if this is the main grid
    if (this.isMainGrid()) {
      this.updateUrlHistory();
      if (this.allowDebug()) {
        console.log("Updated URL history (main grid)");
      }
    } else {
      if (this.allowDebug()) {
        console.log("Skipped URL history update (non-main grid)");
      }
    }

    // Scroll to appropriate position based on grid type
    this.scrollToGrid();

    await this.loadData();
  }

  /**
   * Handle sort change events from FilterDrawer
   * Debounced to prevent rapid re-renders
   * @param {CustomEvent} event
   * @private
   */
  async handleSortChange(event) {
    // Check if this event is intended for this ProductGrid
    if (!this.shouldRespondToFilterDrawerEvent(event)) {
      if (this.allowDebug()) {
        console.log("handleSortChange: Ignoring event - not for this grid", {
          eventIdentifier: event.detail?.identifier,
          gridId: this.config.id
        });
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("handleSortChange", event);
    }
    const { sortBy } = event.detail;
    this.config.sortBy = sortBy;
    this.state.currentPage = 1; // Reset to first page

    // Debounce sort changes to prevent rapid re-renders
    // Clear any existing timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }

    // Set new timer (300ms debounce)
    this.filterDebounceTimer = setTimeout(async () => {
      // Update URL history before loading data
      this.updateUrlHistory();

      await this.loadData();

      // Clear timer reference
      this.filterDebounceTimer = null;
    }, 300);
  }

  /**
   * Handle clear filters events from FilterDrawer
   * Debounced to prevent rapid re-renders
   * @param {CustomEvent} event
   * @private
   */
  async handleClearFilters(event) {
    // Check if this event is intended for this ProductGrid
    if (!this.shouldRespondToFilterDrawerEvent(event)) {
      if (this.allowDebug()) {
        console.log("handleClearFilters: Ignoring event - not for this grid", {
          eventIdentifier: event.detail?.identifier,
          gridId: this.config.id
        });
      }
      return;
    }

    if (this.allowDebug()) {
      console.log("handleClearFilters", event);
    }
    this.state.activeFilters = {};
    this.state.currentPage = 1;

    // Debounce clear filters to prevent rapid re-renders
    // Clear any existing timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }

    // Set new timer (300ms debounce)
    this.filterDebounceTimer = setTimeout(async () => {
      // Update URL history before loading data
      this.updateUrlHistory();

      await this.loadData();

      // Clear timer reference
      this.filterDebounceTimer = null;
    }, 300);
  }

  /**
   * Handle vendor change events from theme utilities
   * @param {CustomEvent} event
   * @private
   */
  async handleVendorChange(event) {
    if (this.hasContextVendor()) {
      return;
    }

    if (this.allowDebug()) {
      console.log("handleVendorChange", event);
    }

    const { vendor } = event.detail;

    if (this.allowDebug()) {
      console.log(`ProductGrid: Search vendor changed to ${vendor}, reloading data...`);
    }

    // Reset to first page when vendor changes
    this.state.currentPage = 1;

    // Reload data with new vendor
    await this.loadData();
  }

  /**
   * Handle processing flag changes from theme utilities
   * @param {CustomEvent} event
   * @private
   */
  async handleProcessingChange(event) {
    if (this.allowDebug()) {
      console.log("handleProcessingChange", event);
    }

    const { enabled } = event.detail;

    if (this.allowDebug()) {
      console.log(`ProductGrid: Product processing ${enabled ? 'enabled' : 'disabled'}, reloading data...`);
    }

    // Reset to first page when processing changes
    this.state.currentPage = 1;

    // Reload data with new processing logic
    await this.loadData();
  }

  /**
   * Handle filter changes from facets.js for compatibility
   * @param {CustomEvent} event
   * @private
   */
  async handleFacetsFilterChange(event) {
    if (this.allowDebug()) {
      console.log("handleFacetsFilterChange", event.detail);
    }

    const { urlParameters } = event.detail;

    // Parse URL parameters and update activeFilters
    this.parseUrlParametersToActiveFilters(urlParameters);

    // Update filtered state (parseUrlParametersToActiveFilters already calls updateFilteredState, but ensure it's set)
    this.updateFilteredState();

    // Reset to first page when filters change
    this.state.currentPage = 1;

    // Update URL history before loading data
    this.updateUrlHistory();

    await this.loadData();
  }

  /**
   * Parse URL parameters and update activeFilters state
   * @param {string|null} urlParameters - URL search parameters string, or null to use current location
   * @private
   */
  parseUrlParametersToActiveFilters(urlParameters = null) {
    // Only read from URL if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      this.state.activeFilters = {};
      if (this.allowDebug()) {
        console.log("ProductGrid: leverageUrl is false, not reading filters from URL");
      }
      return;
    }

    // Use shared utility function to parse URL parameters
    this.state.activeFilters = parseUrlParametersToActiveFilters(urlParameters, {
      debug: this.allowDebug()
    });

    // Update filtered state after parsing URL parameters
    this.updateFilteredState();
  }

  /**
   * Initialize component state from URL parameters
   * @private
   */
  initStateFromUrl() {
    // Only read from URL if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      if (this.allowDebug()) {
        console.log("ProductGrid: leverageUrl is false, not initializing state from URL");
      }
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    // Initialize pagination from URL
    const pageParam = urlParams.get("page");
    if (pageParam) {
      this.state.currentPage = parseInt(pageParam, 10) || 1;
    }

    // Initialize sort from URL
    const sortParam = urlParams.get("sort_by");
    if (sortParam) {
      this.config.sortBy = sortParam;
    } else if (!this.config.sortBy && this.config.defaultSortBy) {
      // Use default_sort_by from config if no URL param and no existing sort_by
      this.config.sortBy = this.config.defaultSortBy;
    }

    // Initialize search terms from URL (for search context)
    if (this.config.context === "search") {
      const searchTerms = urlParams.get("q");
      if (searchTerms) {
        this.config.terms = searchTerms;
      }

      const searchType = urlParams.get("type");
      if (searchType) {
        this.config.type = searchType;
      }
    }

    if (this.allowDebug()) {
      console.log("Initialized state from URL:", {
        currentPage: this.state.currentPage,
        sortBy: this.config.sortBy,
        terms: this.config.terms,
        type: this.config.type,
      });
    }

    // Update filtered state after initializing from URL
    this.updateFilteredState();
  }


  /**
   * Public method to refresh data
   * @returns {Promise<void>}
   */
  async refresh() {
    if (this.allowDebug()) {
      console.log("refresh", this);
    }
    await this.loadData();
  }

  /**
   * Public method to update search terms (for search context)
   * @param {string} terms
   * @returns {Promise<void>}
   */
  async updateSearchTerms(terms) {
    if (this.config.context !== "search") return;

    this.config.terms = terms;
    this.state.currentPage = 1;
    this.state.activeFilters = {};

    if (terms && terms.trim().length > 0) {
      await this.loadData();
    } else {
      this.showEmptyState();
    }
  }

  /**
   * Public method to update collection (for collection context)
   * @param {string} handle
   * @returns {Promise<void>}
   */
  async updateCollection(handle) {
    if (this.config.context !== "collection") return;

    this.config.handle = handle;
    this.state.currentPage = 1;
    this.state.activeFilters = {};

    await this.loadData();
  }

  /**
   * Set collection title (useful for collection context)
   * @param {string} title
   */
  setCollectionTitle(title) {
    this.config.collectionTitle = title;
  }

  /**
   * Dispatch data update event to notify filter drawer
   * @param {Object} data - Updated data
   * @private
   */
  dispatchDataUpdateEvent(data) {
    if (this.allowDebug()) {
      console.log("Dispatching data update event:", data);
    }

    const event = new CustomEvent("product-grid:data-updated", {
      detail: { data },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Update the browser URL history with current state
   * @private
   */
  updateUrlHistory() {
    // Skip URL updates if leverageUrl is disabled
    if (this.config.leverageUrl === false) {
      if (this.allowDebug()) {
        console.log("ProductGrid: leverageUrl is false, skipping URL history update");
      }
      return;
    }

    try {
      const newUrl = this.buildCurrentStateUrl();
      const currentUrl = window.location.href;

      // Only update history if the URL has actually changed
      if (newUrl !== currentUrl) {
        // Use pushState to update URL without page reload
        // This allows back/forward button functionality
        window.history.pushState(
          {
            filters: this.state.activeFilters,
            page: this.state.currentPage,
            sortBy: this.config.sortBy,
            context: this.config.context,
            terms: this.config.terms,
          },
          "",
          newUrl
        );

        if (this.allowDebug()) {
          console.log("Updated URL history:", newUrl);
        }
      }
    } catch (error) {
      console.error("Failed to update URL history:", error);
    }
  }

  /**
   * Build URL that represents current component state
   * @returns {string}
   * @private
   */
  buildCurrentStateUrl() {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams();

    // Add search/collection specific parameters
    if (this.config.context === "search") {
      if (this.config.terms) {
        params.set("q", this.config.terms);
      }
      if (this.config.type) {
        params.set("type", this.config.type);
      }
    }

    // Add pagination
    if (this.state.currentPage && this.state.currentPage > 1) {
      params.set("page", this.state.currentPage);
    }

    // Add sort parameter
    const defaultSort = this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance");
    if (this.config.sortBy && this.config.sortBy !== defaultSort) {
      params.set("sort_by", this.config.sortBy);
    }

    // Add active filters
    if (this.state.activeFilters) {
      Object.entries(this.state.activeFilters).forEach(([key, values]) => {
        if (Array.isArray(values)) {
          values.forEach((value) => {
            params.append(key, value);
          });
        }
      });
    }

    // Build the full URL
    currentUrl.search = params.toString();
    return currentUrl.toString();
  }

  /**
   * Handle browser back/forward button navigation
   * @param {PopStateEvent} event
   * @private
   */
  async handlePopState(event) {
    if (this.allowDebug()) {
      console.log("handlePopState", event);
    }

    // Only handle popstate if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      if (this.allowDebug()) {
        console.log("ProductGrid: leverageUrl is false, ignoring popstate event");
      }
      return;
    }

    // Re-parse URL parameters to update component state
    const urlParams = new URLSearchParams(window.location.search);

    // Update search terms if in search context
    if (this.config.context === "search") {
      const newTerms = urlParams.get("q") || "";
      if (newTerms !== this.config.terms) {
        this.config.terms = newTerms;
      }
    }

    // Update pagination
    const pageParam = urlParams.get("page");
    this.state.currentPage = pageParam ? parseInt(pageParam, 10) : 1;

    // Update sort
    const sortParam = urlParams.get("sort_by");
    const defaultSort = this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance");
    this.config.sortBy = sortParam || defaultSort;

    // Update active filters from URL (this now properly resets filters)
    this.parseUrlParametersToActiveFilters();

    // Reload data with new state (don't update URL history again)
    await this.loadData();

    // Ensure FilterDrawer is notified of the state change (especially for empty filter states)
    if (this.state.data) {
      this.dispatchDataUpdateEvent(this.state.data);
    }
  }
}

customElements.define("product-grid", ProductGrid);